import { ReactNode, useState } from "react";
import Button from "../../../student/components/generic/button";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { useDataTable } from "../DataTable/DataTableContext";
import { CourseGroup } from "../../../learner/types";

type FormInputs = Pick<CourseGroup, "name" | "description">;

export const CreateCourseGroupModal: React.FC<{
  onSave: () => void;
  groupToEdit?: CourseGroup;
  children: (onClick: () => void) => ReactNode;
}> = ({ onSave, groupToEdit, children }) => {
  const { table } = useDataTable();
  const [isVisible, setIsVisible] = useState(false);
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormInputs>(groupToEdit ? { defaultValues: groupToEdit } : {});

  const { mutate, isPending } = useMutation({
    mutationFn: async (group: FormInputs) => {
      const maxOrder = Math.max(
        1,
        ...table.rows.map((row) => row.original.order as number)
      );

      return executeQuery({
        path: "/manager_new/parent-portal/course-groups",
        method: "post",
        requestBody: {
          group: groupToEdit
            ? {
                ...groupToEdit,
                ...group,
              }
            : {
                ...group,
                order: maxOrder + 1,
              },
        },
      });
    },
  });

  const onSubmit = (data: FormInputs) => {
    setNonFieldError(null);
    mutate(data, {
      onSuccess: () => {
        setIsVisible(false);
        reset();
        onSave();
      },
      onError: () =>
        setNonFieldError("An error occurred while creating the course group"),
    });
  };

  return (
    <>
      <div>{children(() => setIsVisible(true))}</div>

      <DMModal visible={isVisible} onClose={() => setIsVisible(false)}>
        <form
          className="flex flex-col gap-10"
          onSubmit={handleSubmit(onSubmit)}
        >
          <DMModalTitle>
            {groupToEdit ? "Edit Course Group" : "Create a New Course Group"}
          </DMModalTitle>
          <DMModalContent>
            <div className="flex w-full flex-col gap-1">
              <label className="text-sm" htmlFor="name">
                Name
              </label>
              <input
                type="text"
                id="name"
                className="h-10 rounded px-2 py-0"
                {...register("name", { required: "This field is required" })}
              />
              {errors.name && (
                <span className="text-sm text-dm-error-500">
                  {errors.name.message}
                </span>
              )}
            </div>

            <div className="flex w-full flex-col gap-1">
              <label className="text-sm" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                className="h-16 w-full resize-y rounded p-2"
                {...register("description", {
                  required: "This field is required",
                })}
              />
              {errors.description && (
                <span className="text-sm text-dm-error-500">
                  {errors.description.message}
                </span>
              )}
            </div>

            {nonFieldError && (
              <p className="w-full rounded bg-dm-error-100 px-4 py-2 text-center text-sm text-dm-error-500">
                {nonFieldError}
              </p>
            )}
          </DMModalContent>
          <DMModalFooter>
            <Button
              type="outline"
              onClick={() => setIsVisible(false)}
              submit={false}
            >
              Cancel
            </Button>
            <Button isLoading={isPending}>Save</Button>
          </DMModalFooter>
        </form>
      </DMModal>
    </>
  );
};
